import styled from "styled-components";
import { CTA_HIDE_MS, PILL_WIDTH_ANIMATION_MS } from "./animationTiming";

const PILL_ANIMATION_DURATION = `${PILL_WIDTH_ANIMATION_MS}ms`;
const CTA_ANIMATION_DURATION = `${CTA_HIDE_MS}ms`;
const PILL_ANIMATION_EASING = "ease-in-out";

export const Container = styled.div(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    width: "100%",
    marginTop: "6px",
    backgroundColor: theme.colors[theme.colorMode].background_Primary,
    padding: `0px ${theme.spacing.medium}`,
}));

export const Content = styled.div(() => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: "46px",
    width: "100%",
}));

/**
 * Pill shape, clipping, and width animation. Collapsed/expanded looks are painted by the
 * surface layers below (CSS cannot transition between two gradient backgrounds).
 * Fixed px widths while animating; `auto` when settled so content is never clipped.
 */
export const SavingsPill = styled.div<{
    isVisibility?: boolean;
    isExpanded?: boolean;
    isLayoutAnimating?: boolean;
    collapsedMaxWidthPx?: number;
    expandedMaxWidthPx?: number;
}>(({ theme, isVisibility, isExpanded, isLayoutAnimating, collapsedMaxWidthPx = 0, expandedMaxWidthPx = 0 }) => {
    const getWidth = () => {
        if (isExpanded) {
            return `${expandedMaxWidthPx}px`;
        }
        // px↔px interpolates; `auto`↔px does not.
        if (isLayoutAnimating) {
            return `calc(${collapsedMaxWidthPx}px + ${theme.spacing.xSmall} * 2)`;
        }
        return "auto";
    };

    return {
        position: "relative",
        display: "flex",
        justifyContent: "flex-start",
        marginRight: theme.spacing.xSmall,
        alignItems: "center",
        visibility: isVisibility ? "visible" : "hidden",
        flexShrink: 0,
        boxSizing: "border-box",
        padding: `${theme.spacing.xxSmall} ${theme.spacing.xSmall}`,
        borderRadius: theme.cornerRadius.medium,
        // Clip during expand/collapse so copy swapped at tween start is uncovered/covered by width.
        overflow: isLayoutAnimating || isExpanded ? "hidden" : "visible",
        whiteSpace: "nowrap",
        width: getWidth(),
        transition: isLayoutAnimating ? `width ${PILL_ANIMATION_DURATION} ${PILL_ANIMATION_EASING}` : "none",
    };
});

const surfaceLayerBase = {
    position: "absolute" as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: "inherit",
    boxSizing: "border-box" as const,
    pointerEvents: "none" as const,
    transition: `opacity ${PILL_ANIMATION_DURATION} ${PILL_ANIMATION_EASING}`,
};

/** Collapsed fill + solid border. Fades out when expanded. */
export const PillSolidSurface = styled.div<{ isExpanded?: boolean }>(({ theme, isExpanded }) => ({
    ...surfaceLayerBase,
    background: theme.colors[theme.colorMode].positive_Sub,
    border: `1px solid ${theme.colors[theme.colorMode].border_Tertiary}`,
    opacity: isExpanded ? 0 : 1,
}));

/**
 * Expanded fill. Solid border by default; engines with `mask-composite: exclude` get a
 * left-to-right fade-out ring instead.
 */
export const PillGradientSurface = styled.div<{ isExpanded?: boolean }>(({ theme, isExpanded }) => ({
    ...surfaceLayerBase,
    background: "linear-gradient(90deg, #C8F9E5 50%, rgba(200, 249, 229, 0.00) 100%)",
    opacity: isExpanded ? 1 : 0,
    border: `1px solid ${theme.colors[theme.colorMode].border_Tertiary}`,
    "@supports (mask-composite: exclude)": {
        border: "none",
        "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            borderRadius: "inherit",
            padding: "1px",
            background: `linear-gradient(90deg, ${theme.colors[theme.colorMode].border_Tertiary} 0%, transparent 100%)`,
            WebkitMask: "linear-gradient(#fff, #fff) content-box, linear-gradient(#fff, #fff)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            pointerEvents: "none",
        },
    },
}));

export const PillContent = styled.div({
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
});

/** Off-screen node used to measure natural text width. */
export const HiddenMeasure = styled.div({
    position: "absolute",
    left: -9999,
    top: 0,
    visibility: "hidden",
    pointerEvents: "none",
    whiteSpace: "nowrap",
});

export const HeaderCta = styled.div<{
    isHidden?: boolean;
    isLoading?: boolean;
    contentMaxWidthPx?: number;
    animateMaxWidth?: boolean;
}>(({ isHidden, isLoading, contentMaxWidthPx = 0, animateMaxWidth = false }) => {
    let opacity = 1;
    if (isHidden) {
        opacity = 0;
    } else if (isLoading) {
        opacity = 0.5;
    }

    // Without animateMaxWidth, skip max-width so first mount does not wipe in from the right.
    let maxWidth: number | string = "none";
    if (isHidden) {
        maxWidth = 0;
    } else if (animateMaxWidth) {
        maxWidth = contentMaxWidthPx;
    }

    return {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0,
        marginLeft: "auto",
        maxWidth,
        opacity,
        // Clip while collapsing/expanding width so the wipe-in does not spill.
        overflow: isHidden || animateMaxWidth ? "hidden" : "visible",
        whiteSpace: "nowrap",
        pointerEvents: isLoading || isHidden ? "none" : "auto",
        // Opacity always; max-width only during sequence hide/reveal (not first mount).
        transition: animateMaxWidth
            ? `opacity ${CTA_ANIMATION_DURATION} ${PILL_ANIMATION_EASING}, max-width ${CTA_ANIMATION_DURATION} ${PILL_ANIMATION_EASING}`
            : `opacity ${CTA_ANIMATION_DURATION} ${PILL_ANIMATION_EASING}`,
    };
});

// Dashed horizontal separator shown between the quick-checkout header and footer.
export const Divider = styled.div(({ theme }) => ({
    width: "100%",
    borderTop: `1px dashed ${theme.colors[theme.colorMode].background_Button_Disabled}`,
}));

export const IconWrapper = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing.xxSmall,
    borderRadius: "50%",
    backgroundColor: theme.colors[theme.colorMode].background_Secondary,
    marginLeft: theme.spacing.xxSmall,
}));

export const DeliveryCountTextWrapper = styled.div<{
    isHidden?: boolean;
}>(({ isHidden }) => ({
    display: isHidden ? "none" : "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    whiteSpace: "nowrap",
}));
